<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <h4 class="modal-card-title" >
        {{ viewListDate }}
      </h4>
    </header>

    <section class="modal-card-body">
      <p class="has-text-black-bis has-text-weight-semibold mb-3">Orders</p>
      <div class="calendar-table-wrapper">
        <data-table
          :key="tableKey"
          :apiMode="false"
          :tableOpts="tableOpts"
          :loadData="loadTableData"
          @cell-clicked="onTableCellClicked"
        />
      </div>
    </section>

    <footer class="modal-card-foot is-justify-content-flex-end">
      <button class="button is-outlined" @click="closeModal">Close</button>
    </footer>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import moment from 'moment';
import { find } from 'lodash';
import { useToast } from 'vue-toastification';
import DataTable from '@/components/table-fields/DataTable.vue';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['closeModal']);
const store = useStore();
const router = useRouter();
const toast = useToast();

const stageLabelMap = {
  manufacturing: 'Manufacture By',
  poManufactureBy: 'Manufacture By',
  detailing: 'Detail By',
  poDetailBy: 'Detail By',
  qa: 'Qa By',
  poQaBy: 'Qa By',
  coordination: 'Coordinate By',
  delivery: 'OnSite',
};
const stageDateMap = {
  manufacturing: 'manufactureBy',
  poManufactureBy: 'manufactureBy',
  detailing: 'detailBy',
  poDetailBy: 'detailBy',
  qa: 'qaBy',
  poQaBy: 'qaBy',
  coordination: 'coordinationBy',
  delivery: 'deliver',
};
const viewListDate = ref('');
const listOrders = ref([]);
const tableKey = ref(0);

const tableOpts = {
  defaultSortField: 'name',
  defaultSortOrder: 'asc',
  isCheckable: false,
  fields: [
    {
      title: 'Name',
      id: 'name',
      prop: 'name',
      width: '280px',
      visible: true,
      defaultVisible: true,
      openCard: true,
    },
    {
      title: 'Owner',
      id: 'owner',
      prop: 'owner',
      width: '220px',
      visible: true,
      defaultVisible: true,
    },
    {
      title: 'Project',
      id: 'project',
      prop: 'project',
      width: '220px',
      visible: true,
      defaultVisible: true,
    },
    {
      title: 'Current Stage',
      id: 'currentStage',
      prop: 'currentStage',
      width: '220px',
      visible: true,
      defaultVisible: true,
    },
    {
      title: 'Date',
      id: 'date',
      prop: 'date',
      width: '140px',
      visible: true,
      defaultVisible: true,
    },
  ],
};

const getOrderEvent = (order) => order?.event || order;
const getOrderDetails = (order) => getOrderEvent(order)?.extendedProps || {};

const normalizeOrderToEvent = (order, defaultDateStage, defaultStartDate) => {
  const eventOrder = getOrderEvent(order);
  if (!eventOrder) return null;
  if (eventOrder.extendedProps) return eventOrder;

  const dateStage = defaultDateStage || 'coordination';
  const dateKey = stageDateMap[dateStage] || 'detailBy';
  const boDates = eventOrder?.isManager ? eventOrder?.manager?.simpleDates : eventOrder?.simpleDates;
  const stageDate = boDates?.[dateKey]?.value ? new Date(boDates[dateKey].value) : new Date(defaultStartDate);

  return {
    id: eventOrder._id || eventOrder.id,
    title: eventOrder.name || '-',
    start: stageDate,
    extendedProps: {
      dateStage,
      financialId: eventOrder.financialId || '-',
      owner: eventOrder.isManager
        ? eventOrder?.manager?.owner?.user?.name
        : eventOrder?.owner?.user?.name,
      __t: eventOrder.__t,
      stage: eventOrder.stage,
      isManager: eventOrder.isManager,
      project: eventOrder?.project?.name,
      projectId: eventOrder?.project?._id,
      onsite: eventOrder?.simpleDates?.deliver
        ? moment(eventOrder.simpleDates.deliver.value).format('MM/DD/YYYY')
        : '',
      companyId: eventOrder?.owner?.company?.id,
      openStage: eventOrder.stage,
      tags: eventOrder.isManager ? eventOrder?.manager?.tags : eventOrder?.tags,
    },
  };
};

const getFormattedDate = (date) => moment(date).format('MM/DD/YYYY');

const getCurrentStageLabel = (event) => {
  const details = getOrderDetails(event);
  return details.stage || '-';
};

const getDateWithLabel = (event) => {
  const details = getOrderDetails(event);
  const label = stageLabelMap[details.dateStage] || '';
  const date = getFormattedDate(event.start);
  return label ? `${date} (${label})` : date;
};

const tableRows = computed(() => listOrders.value.map((event, index) => {
  const details = getOrderDetails(event);
  return {
    _id: `${event.id || index}-${details.dateStage || ''}`,
    sourceId: event.id,
    name: event.title || '-',
    owner: details.owner || '-',
    project: details.project || '-',
    currentStage: getCurrentStageLabel(event),
    date: getDateWithLabel(event),
  };
}));

const loadTableData = async () => tableRows.value;

const getPermissionModuleForEvent = (event) => {
  const pModule = store.getters.getStageWiseModule(getOrderDetails(event));
  return pModule || '';
};

const closeModal = () => {
  emit('closeModal');
};

const getRouteName = (details, permissionModule) => {
  if (!details.__t) return '';
  if (permissionModule === 'production-manager') return 'manager-edit';
  if (permissionModule === 'material-manager') return 'material-edit';
  if (details.__t === 'ProductionOrder') return 'order-edit';
  if (details.__t === 'Prefabs') return 'prefab-edit';
  return '';
};

const openCard = async (event) => {
  const lookUpOrder = getOrderEvent(event);
  const details = getOrderDetails(lookUpOrder);
  if (
    find(store.state.queryParams._projects, { _id: details.projectId })
    && store.state.userData.company === details.companyId
  ) {
    const routeName = getRouteName(details, getPermissionModuleForEvent(lookUpOrder));
    if (!routeName) return;
    const routeParams = {
      name: routeName,
      params: {
        projectId: details.projectId,
        cardId: lookUpOrder.id,
      },
    };
    if (['manager-edit', 'material-edit'].includes(routeName)) {
      routeParams.params.stage = details.openStage || details.stage;
    }
    await router.push(routeParams);
  } else {
    toast.error('You dont have permission to open this order');
  }
};

const onTableCellClicked = async (payload) => {
  if (payload?.type !== 'openCard') return;
  const rowId = payload?.data?._id;
  const matchedRow = find(tableRows.value, { _id: rowId });
  if (!matchedRow) return;
  const matchedEvent = find(listOrders.value, { id: matchedRow.sourceId });
  if (!matchedEvent) return;
  await openCard(matchedEvent);
};

const initModalState = () => {
  const activeOrder = getOrderEvent(props.order);
  if (!activeOrder) return;

  viewListDate.value = getFormattedDate(activeOrder.start);

  if (activeOrder?.extendedProps?.heatView) {
    viewListDate.value = moment(activeOrder.start).format('MMM DD, YYYY');
    const defaultDateStage = activeOrder?.extendedProps?.dateStage;
    const heatViewOrders = activeOrder.extendedProps.orders || [];
    listOrders.value = heatViewOrders
      .map((order) => normalizeOrderToEvent(order, defaultDateStage, activeOrder.start))
      .filter(Boolean);
    tableKey.value += 1;
    return;
  }

  const normalizedOrder = normalizeOrderToEvent(activeOrder, activeOrder?.extendedProps?.dateStage, activeOrder.start);
  listOrders.value = normalizedOrder ? [normalizedOrder] : [];
  tableKey.value += 1;
};

onMounted(() => {
  initModalState();
});
</script>

<style scoped>
.calendar-stage {
  display: inline-block;
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  text-transform: capitalize;
  font-weight: 600;
}
</style>
